import { useState, SyntheticEvent } from "react";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
import Icon from "@Components/ui/icon/Icon";
import AccordionButton from "./AccordionButton";
import classes from "./CustomizedAccordion.module.scss";

export interface Props {
  id: number;
  data: {
    question: string;
    answer: string;
  };
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  width: "100%",
  maxWidth: "47.375rem",
  margin: "0 auto",
  ".Mui-expanded": {
    backgroundColor: "#f5f5f5",
  },
  "&:before": {
    display: "none",
  },
}));

export default function CustomizedAccordion({ id, data }: Props) {
  const { question, answer } = data || {};
  const [expanded, setExpanded] = useState<number | false | undefined>(
    undefined
  );

  const AccordionDetails = styled(MuiAccordionDetails)(() => ({
    width: "100%",
    maxWidth: "47.375rem",
    margin: "0 auto",
    position: "relative",
    overflowX: "hidden",
    overflowY: "auto",
    borderTop: "none",
    display: "block",
    height: "100%",
    paddingBottom: "40px",
    opacity: "1",
    backgroundColor: expanded ? "#f5f5f5" : "fffff",
    marginTop: "-15px",
    transition: "height .5s ease,opacity .5s ease",
    "h3.price-estimate-notice__title": {
      fontSize: ".875rem !important",
      margin: "0 !important",
      width: "100% !important",
      maxWidth: "100% !important",
    },
    "p.price-estimate-notice__body": {
      fontSize: ".75rem",
      margin: "0 !important",
    },
    "div.rich-media-item": {
      margin: 0,
      padding: 0,
      position: "relative",
      height: "100%",
      width: "100%",
    },
  }));

  const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <>
      <MuiAccordionSummary
        expandIcon={<AccordionButton active={expanded} />}
        {...props}
      />
    </>
  ))(() => ({
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    maxWidth: "47.375rem",
    margin: "0 auto",
    padding: 0,
    "@media (min-width: 60em)": {
      minHeight: "100px",
    },
  }));

  const handleChange =
    (panel: number) => (event: SyntheticEvent, newExpanded: boolean) => {
      event.preventDefault();
      setExpanded(newExpanded ? panel : false);
    };

  // added to control accordion when click anywhere in card, to replicate production site
  const closeOnClickOutside = () => {
    const selection = window?.getSelection() || "";

    if (selection?.toString().length) {
      return;
    }

    setExpanded(false);
  };
  const openOnClickOutside = (panel: number) => setExpanded(panel);

  return (
    <div
      data-testid="div-button"
      className={classes["faq-container"]}
      onClick={
        expanded === id ? closeOnClickOutside : () => openOnClickOutside(id)
      }
    >
      <div className={classes["faq"] + " " + classes[expanded ? "active" : ""]}>
        <Accordion expanded={expanded === id} onChange={handleChange(id)}>
          <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
            <div className={classes["icon"]} data-content={id}>
              <Icon iconName="question-bullet" />
            </div>
            <h3>{question}</h3>
          </AccordionSummary>
          <AccordionDetails>
            <div
              className={classes["answer-text"]}
              dangerouslySetInnerHTML={{ __html: answer }}
            />
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
}
